/**
 * TanStack Query hooks for chat state management.
 *
 * All server-state for chats flows through these hooks.
 * Zustand (useChatStore) handles only ephemeral UI state.
 *
 * Hooks:
 *   - useChats: infinite paginated chat list
 *   - useChat: single chat detail
 *   - useChatMessages: infinite messages within a chat
 *   - useCreateChat: mutation to create chat
 *   - useUpdateChat: mutation to update chat (optimistic)
 *   - useDeleteChat: mutation to delete chat (optimistic + undo)
 *   - useSearchChats: debounced search
 */

import {
  useInfiniteQuery,
  useQuery,
  useMutation,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';
import {
  fetchChats,
  fetchChat,
  fetchMessages,
  createChat,
  updateChat,
  deleteChat,
  searchChats,
} from '@/services/chats';
import { useDebounce } from '@/hooks/useDebounce';
import { useChatStore } from '@/stores/useChatStore';
import { useShallow } from 'zustand/react/shallow';
import type {
  ChatSummary,
  ChatDetailed,
  Message,
  CreateChatInput,
  UpdateChatInput,
  SearchChatsInput,
  PaginatedResponse,
  ListChatsParams,
} from '@/types/chat.types';

// ---------------------------------------------------------------------------
// Query Keys
// ---------------------------------------------------------------------------

export const chatKeys = {
  all: ['chats'] as const,
  lists: () => [...chatKeys.all, 'list'] as const,
  list: (params?: Omit<ListChatsParams, 'cursor'>) =>
    [...chatKeys.lists(), params] as const,
  detail: (id: string) => [...chatKeys.all, 'detail', id] as const,
  messages: (chatId: string) =>
    [...chatKeys.all, 'detail', chatId, 'messages'] as const,
  search: (query: string) => [...chatKeys.all, 'search', query] as const,
};

// ---------------------------------------------------------------------------
// Types for infinite data
// ---------------------------------------------------------------------------

type ChatListInfiniteData = InfiniteData<
  PaginatedResponse<ChatSummary>,
  string | undefined
>;

// ---------------------------------------------------------------------------
// useChats -- infinite paginated chat list
// ---------------------------------------------------------------------------

export interface UseChatListParams {
  status?: 'active' | 'archived';
  pinned?: boolean;
  model_id?: string;
  agent_id?: string;
  include_classifications?: boolean;
  topic_id?: string;
}

// Page size for the sidebar list. Keeps the initial render visually short
// and bounded; the "Carregar mais" button pulls additional batches.
export const CHAT_LIST_PAGE_SIZE = 10;

export function useChats(params?: UseChatListParams) {
  return useInfiniteQuery({
    queryKey: chatKeys.list(params),
    queryFn: ({ pageParam }) =>
      fetchChats({ ...params, cursor: pageParam, limit: CHAT_LIST_PAGE_SIZE }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage: PaginatedResponse<ChatSummary>) =>
      lastPage.has_more ? (lastPage.next_cursor ?? undefined) : undefined,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
}

// ---------------------------------------------------------------------------
// useChat -- single chat with first page of messages
// ---------------------------------------------------------------------------

export function useChat(chatId: string | null) {
  return useQuery({
    queryKey: chatKeys.detail(chatId ?? ''),
    queryFn: () => fetchChat(chatId!),
    enabled: !!chatId,
    staleTime: 60_000,
  });
}

// ---------------------------------------------------------------------------
// useChatMessages -- infinite messages (reverse: load older on scroll up)
// ---------------------------------------------------------------------------

// Page size for the message list. The first page is the most recent 10
// messages — MessageList auto-scrolls to the bottom on mount. Scrolling
// near the top triggers the infinite query to fetch the next 10 older.
export const MESSAGE_LIST_PAGE_SIZE = 10;

export function useChatMessages(
  chatId: string | null,
  refetchInterval: number | false = false,
) {
  return useInfiniteQuery({
    queryKey: chatKeys.messages(chatId ?? ''),
    queryFn: ({ pageParam }) =>
      fetchMessages(chatId!, {
        cursor: pageParam,
        direction: 'before',
        limit: MESSAGE_LIST_PAGE_SIZE,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage: PaginatedResponse<Message>) =>
      lastPage.has_more ? (lastPage.next_cursor ?? undefined) : undefined,
    enabled: !!chatId,
    staleTime: 60_000,
    refetchInterval,
  });
}

// ---------------------------------------------------------------------------
// useCreateChat
// ---------------------------------------------------------------------------

export function useCreateChat() {
  const queryClient = useQueryClient();
  const setActiveChat = useChatStore((s) => s.setActiveChat);

  return useMutation({
    mutationFn: (input: CreateChatInput) => createChat(input),

    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: chatKeys.lists() });

      const previousLists =
        queryClient.getQueriesData<ChatListInfiniteData>({
          queryKey: chatKeys.lists(),
        });

      // Optimistic: prepend a temp chat to the first page
      const tempId = `temp-${Date.now()}`;
      const tempChat: ChatSummary = {
        id: tempId,
        title: input.title ?? null,
        model_id: input.model_id ?? null,
        model_display_name: null,
        agent_id: input.agent_id ?? null,
        agent_name: null,
        status: 'active',
        pinned: false,
        message_count: 0,
        last_message_at: null,
        total_cost_usd: '0',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      queryClient.setQueriesData<ChatListInfiniteData>(
        { queryKey: chatKeys.lists() },
        (old) => {
          if (!old || old.pages.length === 0) return old;
          const firstPage = old.pages[0]!;
          const restPages = old.pages.slice(1);
          return {
            ...old,
            pages: [
              {
                data: [tempChat, ...firstPage.data],
                next_cursor: firstPage.next_cursor,
                has_more: firstPage.has_more,
              },
              ...restPages,
            ],
          };
        },
      );

      return { previousLists, tempId };
    },

    onSuccess: (data, _input, context) => {
      // Replace temp entry with real data
      if (context?.tempId) {
        queryClient.setQueriesData<ChatListInfiniteData>(
          { queryKey: chatKeys.lists() },
          (old) => {
            if (!old) return old;
            return mapPages(old, (chats) =>
              chats.map((chat) =>
                chat.id === context.tempId
                  ? chatDetailedToSummary(data)
                  : chat,
              ),
            );
          },
        );
      }

      // Set the new chat as active
      setActiveChat(data.id);

      // Populate detail cache
      queryClient.setQueryData(chatKeys.detail(data.id), data);
    },

    onError: (_err, _input, context) => {
      if (context?.previousLists) {
        for (const [key, data] of context.previousLists) {
          queryClient.setQueryData(key, data);
        }
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: chatKeys.lists() });
    },
  });
}

// ---------------------------------------------------------------------------
// useUpdateChat
// ---------------------------------------------------------------------------

export function useUpdateChat() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateChatInput }) =>
      updateChat(id, input),

    onMutate: async ({ id, input }) => {
      await queryClient.cancelQueries({ queryKey: chatKeys.lists() });

      const previousLists =
        queryClient.getQueriesData<ChatListInfiniteData>({
          queryKey: chatKeys.lists(),
        });

      // Optimistic update in all list caches
      queryClient.setQueriesData<ChatListInfiniteData>(
        { queryKey: chatKeys.lists() },
        (old) => {
          if (!old) return old;
          return mapPages(old, (chats) =>
            chats.map((chat) =>
              chat.id === id
                ? { ...chat, ...input, updated_at: new Date().toISOString() }
                : chat,
            ),
          );
        },
      );

      return { previousLists };
    },

    onError: (_err, _vars, context) => {
      if (context?.previousLists) {
        for (const [key, data] of context.previousLists) {
          queryClient.setQueryData(key, data);
        }
      }
    },

    onSettled: (_data, _err, { id }) => {
      queryClient.invalidateQueries({ queryKey: chatKeys.lists() });
      queryClient.invalidateQueries({ queryKey: chatKeys.detail(id) });
    },
  });
}

// ---------------------------------------------------------------------------
// useDeleteChat
// ---------------------------------------------------------------------------

export function useDeleteChat() {
  const queryClient = useQueryClient();
  const { activeChatId, setActiveChat } = useChatStore(useShallow((s) => ({
    activeChatId: s.activeChatId,
    setActiveChat: s.setActiveChat,
  })));

  return useMutation({
    mutationFn: (id: string) => deleteChat(id),

    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: chatKeys.lists() });

      const previousLists =
        queryClient.getQueriesData<ChatListInfiniteData>({
          queryKey: chatKeys.lists(),
        });

      // Optimistic remove
      queryClient.setQueriesData<ChatListInfiniteData>(
        { queryKey: chatKeys.lists() },
        (old) => {
          if (!old) return old;
          return mapPages(old, (chats) =>
            chats.filter((chat) => chat.id !== id),
          );
        },
      );

      // Clear active if deleted
      if (activeChatId === id) {
        setActiveChat(null);
      }

      return { previousLists, wasActive: activeChatId === id };
    },

    onError: (_err, _id, context) => {
      if (context?.previousLists) {
        for (const [key, data] of context.previousLists) {
          queryClient.setQueryData(key, data);
        }
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: chatKeys.lists() });
    },
  });
}

// ---------------------------------------------------------------------------
// useSearchChats -- debounced search
// ---------------------------------------------------------------------------

export function useSearchChats(
  query: string,
  params?: Omit<SearchChatsInput, 'query'>,
) {
  const debouncedQuery = useDebounce(query, 300);

  return useQuery({
    queryKey: chatKeys.search(debouncedQuery),
    queryFn: () =>
      searchChats({ query: debouncedQuery, ...params }).then(
        (r) => r.results,
      ),
    enabled: debouncedQuery.length >= 2,
    staleTime: 30_000,
  });
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Map pages preserving PaginatedResponse shape (avoids TS spread issues). */
function mapPages(
  data: ChatListInfiniteData,
  mapper: (chats: ChatSummary[]) => ChatSummary[],
): ChatListInfiniteData {
  return {
    ...data,
    pages: data.pages.map((page) => ({
      data: mapper(page.data),
      next_cursor: page.next_cursor,
      has_more: page.has_more,
    })),
  };
}

function chatDetailedToSummary(chat: ChatDetailed): ChatSummary {
  const {
    messages: _messages,
    total_tokens_in: _ti,
    total_tokens_out: _to,
    ...summary
  } = chat;
  return summary;
}
